/**
 * Month arithmetic for review periods on the client — the mirror of `api/Services/MonthCoverage.cs`
 * for display and form purposes only. The server re-decides everything; these exist so a dialog can
 * say what an edit WILL do before it is saved (spec §4.3, §10.5).
 *
 * Months are `YYYY-MM` strings throughout, which sort chronologically as plain strings. Dates are
 * read off the `YYYY-MM-DD` prefix and never through local-time `Date`, for the reason documented
 * in `calendarDate.ts`.
 */

import i18n from '../i18n';

// Month abbreviations in the UI language (`Aug` / `ago`), read from Intl on the UTC calendar.
const abbrCache = new Map<string, string[]>();
function monthAbbr(): string[] {
  const lang = i18n.language === 'es' ? 'es' : 'en-US';
  let names = abbrCache.get(lang);
  if (!names) {
    const f = new Intl.DateTimeFormat(lang, { month: 'short', timeZone: 'UTC' });
    names = Array.from({ length: 12 }, (_, i) => f.format(new Date(Date.UTC(2000, i, 1))).replace('.', ''));
    abbrCache.set(lang, names);
  }
  return names;
}

/** `2026-08-31T23:59:59Z` → `2026-08`. Null when unreadable. */
export function monthOf(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const m = /^(\d{4})-(\d{2})/.exec(iso);
  return m ? `${m[1]}-${m[2]}` : null;
}

/** `2026-08` → `Aug 2026`. */
export function monthLabel(month: string): string {
  const [y, m] = month.split('-').map(Number);
  return `${monthAbbr()[m - 1]} ${y}`;
}

/** `2026-05..2026-07` → `May – Jul 2026`; a single month → `Aug 2026`; across years carries both. */
export function runLabel(start: string, end: string): string {
  if (start === end) return monthLabel(start);
  const [ys, ms] = start.split('-').map(Number);
  const [ye, me] = end.split('-').map(Number);
  return ys === ye
    ? `${monthAbbr()[ms - 1]} – ${monthAbbr()[me - 1]} ${ye}`
    : `${monthLabel(start)} – ${monthLabel(end)}`;
}

function next(month: string): string {
  const [y, m] = month.split('-').map(Number);
  return m === 12 ? `${y + 1}-01` : `${y}-${String(m + 1).padStart(2, '0')}`;
}

/** Every month from `start` to `end` inclusive. Empty when backwards. */
export function monthsBetween(start: string, end: string): string[] {
  const out: string[] = [];
  for (let m = start; m <= end; m = next(m)) out.push(m);
  return out;
}

/** The inclusive runs of consecutive months in a set, ascending. */
export function contiguousRuns(months: string[]): { start: string; end: string }[] {
  const sorted = [...new Set(months)].sort();
  const runs: { start: string; end: string }[] = [];
  for (const m of sorted) {
    const last = runs[runs.length - 1];
    if (last && next(last.end) === m) last.end = m;
    else runs.push({ start: m, end: m });
  }
  return runs;
}

/** `2026-08` → `2026-08-01`. */
export function firstDayOf(month: string): string {
  return `${month}-01`;
}

/** `2026-02` → `2026-02-28`. Computed in UTC, so no zone can shift it. */
export function lastDayOf(month: string): string {
  const [y, m] = month.split('-').map(Number);
  const day = new Date(Date.UTC(y, m, 0)).getUTCDate();
  return `${month}-${String(day).padStart(2, '0')}`;
}

/** The months a review period touches, from its stored ISO bounds. */
export function monthsOfPeriod(periodStart: string, periodEnd: string): string[] {
  const s = monthOf(periodStart);
  const e = monthOf(periodEnd);
  return s && e ? monthsBetween(s, e) : [];
}

/**
 * What a proposed edit does, in words a reviewer can act on (spec §4.3 / D2): which months the
 * review gains, and which it releases back to generation.
 */
export function describePeriodChange(
  current: { periodStart: string; periodEnd: string },
  proposedStart: string,
  proposedEnd: string
): { covers: string; added: string[]; released: string[] } {
  const before = new Set(monthsOfPeriod(current.periodStart, current.periodEnd));
  const after = monthsBetween(proposedStart, proposedEnd);
  const afterSet = new Set(after);
  return {
    covers: after.length ? runLabel(after[0], after[after.length - 1]) : '',
    added: after.filter(m => !before.has(m)),
    released: [...before].filter(m => !afterSet.has(m)).sort(),
  };
}

/** A month list as words: runs joined with commas, e.g. `May – Jun 2026, Aug 2026`. */
export function monthsAsWords(months: string[]): string {
  return contiguousRuns(months).map(r => runLabel(r.start, r.end)).join(', ');
}

/** The current month in the staff zone (`America/New_York`), as `YYYY-MM`. Month-ends are Eastern. */
export function currentEasternMonth(now: Date = new Date()): string {
  const parts = new Intl.DateTimeFormat('en-CA', { timeZone: 'America/New_York', year: 'numeric', month: '2-digit' })
    .formatToParts(now);
  const y = parts.find(p => p.type === 'year')?.value;
  const m = parts.find(p => p.type === 'month')?.value;
  return `${y}-${m}`;
}

export interface UnfinishedMonth {
  month: string;
  state: 'not-ended' | 'bookkeeping-open' | 'no-task';
  taskSubject?: string | null;
  due?: string | null;
}

/**
 * DP-READY-2 (ruled 2026-10-26: warn, never refuse): of the months a period edit ADDS, those not ready to review.
 * Readiness rows decide where the server gave them; outside them, only "not yet ended" is knowable, from the
 * Eastern calendar. A month with no readiness row that has ended is NOT claimed unready: no claim without a fact.
 */
export function unfinishedAddedMonths(
  added: string[],
  readiness: { month: string; state: string; taskSubject?: string | null; due?: string | null }[] | null,
  now: Date = new Date()
): UnfinishedMonth[] {
  const rows = new Map((readiness ?? []).map(r => [r.month, r]));
  const current = currentEasternMonth(now);
  return [...added].sort().flatMap((month): UnfinishedMonth[] => {
    const r = rows.get(month);
    if (r) {
      return r.state === 'ready'
        ? []
        : [{ month, state: r.state as UnfinishedMonth['state'], taskSubject: r.taskSubject, due: r.due }];
    }
    return month >= current ? [{ month, state: 'not-ended' }] : [];
  });
}
